import type { ThreeElements } from '@react-three/fiber'
import {
  HATCH_COMPACTO,
  raioArcoRoda,
  xDaEstrutura,
  SEDAN_MEDIO,
  ancorasComCinto,
  retencaoDaAncora,
  type PerfilVeiculo,
} from '../../dominio/didatico/perfilVeiculo'
import { CaixaDeRoda3D } from './CaixaDeRoda3D'
import { Carroceria, type DeformacaoFrontalVisual } from './Carroceria'
import { CintoSeguranca } from './CintoSeguranca'
import { EstruturaVeicular } from './EstruturaVeicular'
import { Habitaculo } from './Habitaculo'
import { PortaDidatica } from './PortaDidatica'
import { PainelTerceiraPortaDidatico } from './PainelTerceiraPortaDidatico'
import { SistemaSRS } from './SistemaSRS'
import { ZonasColapsaveis } from './ZonasColapsaveis'
import { Bateria12V } from './Bateria12V'
import { SuspensaoVeicular } from './SuspensaoVeicular'
import { ConjuntoMotriz } from './ConjuntoMotriz'
import { SistemasEnergia } from './SistemasEnergia'
import type { EstadoTetoLateral } from '../../dominio/didatico/rebatimentoLateralTeto'
import type { EstadoElevacaoPainel } from '../../dominio/didatico/elevacaoPainel'
import type { EstadoRebatimentoPainel } from '../../dominio/didatico/rebatimentoPainel'
import type { IdPortaOficina } from '../oficina/zonasOperacaoPorta'
import type { EstadoPortaOficina, OperacaoPorta } from '../../dominio/didatico/oficinaCorte'
import type { EstadoTampaOficina } from '../../dominio/didatico/oficinaPortaMalas'
import type { EstadoTerceiraPortaOficina } from '../../dominio/didatico/oficinaTerceiraPorta'
import type { EstadoDeformacaoVeiculo } from '../../dominio/didatico/campoDeformacao'
import { ProvedorDeformacao } from './contextoDeformacao'
import { DanoEstrutural3D } from './DanoEstrutural3D'

type Props = ThreeElements['group'] & {
  perfil: PerfilVeiculo
  detalhesAnatomia?: boolean
  compressaoDianteira?: number
  compressaoTraseira?: number
  destaqueSuspensao?: boolean
  deformacaoFrontal?: DeformacaoFrontalVisual | null
  /**
   * Deformação completa DESTE veículo (todas as regiões atingidas). Cada
   * veículo recebe o seu estado: o dano de um nunca aparece no outro.
   */
  deformacao?: EstadoDeformacaoVeiculo | null
  /** lataria sólida: estrutura deformada em raio-X, com rótulos de dano */
  raioXEstrutural?: boolean
  /** Quando falso, deixa as rodas disponíveis para serem renderizadas em um grupo externo fixo. */
  mostrarRodas?: boolean
  /** Deslocamento longitudinal em metros; só gira as rodas, sem resolver física extra. */
  distanciaRodada?: number
  /** Quando falso, permite renderizar a suspensão separada da massa suspensa. */
  mostrarSuspensao?: boolean
  /**
   * Mecânica e energia são dezenas de malhas e só interessam quando a aula é
   * sobre elas. Ficam desligadas onde a cena é sobre movimento ou posição —
   * cinemática, Table top, estabilização — e onde há vários veículos na tela.
   */
  mostrarMecanica?: boolean
  mostrarEnergia?: boolean
  oficinaPorta?: {
    id: 'portaDianteiraEsquerda' | 'portaDianteiraDireita' | 'portaTraseiraEsquerda' | 'portaTraseiraDireita'
    estado: EstadoPortaOficina
    /** Progresso da saída da porta retirada, de 0 a 1. */
    progressoSaida: number
    progressoAcao?: number
    operacaoVisual?: OperacaoPorta
  }
  oficinaTampa?: {
    estado: EstadoTampaOficina
  }
  /** Trata a porta traseira do lado indicado como a região da terceira porta, sem dobradiça/trava/vidro reais. */
  oficinaTerceiraPorta?: {
    lado: 'esquerdo' | 'direito'
    estado: EstadoTerceiraPortaOficina
  }
  oficinaExtensor?: {
    deslocamentoPainel: number
    rebatimento?: EstadoRebatimentoPainel
    elevacao?: EstadoElevacaoPainel
    tetoLateral?: EstadoTetoLateral
    idPorta?: IdPortaOficina
  }
  aberturaPortas?: Partial<Record<
    'portaDianteiraEsquerda' | 'portaDianteiraDireita' | 'portaTraseiraEsquerda' | 'portaTraseiraDireita',
    number
  >>
  aberturaTampaTraseira?: number
}

/**
 * Veículo didático completo, montado a partir do perfil.
 *
 * Hatch e sedan compartilham esta composição inteira: só as dimensões e as
 * âncoras mudam. É isso que permite trocar de veículo sem reescrever cinemática,
 * extração ou interação.
 */
export function VeiculoDidatico({
  perfil,
  compressaoDianteira,
  compressaoTraseira,
  destaqueSuspensao,
  detalhesAnatomia = false,
  mostrarRodas = true,
  distanciaRodada = 0,
  mostrarSuspensao = true,
  mostrarMecanica = false,
  mostrarEnergia = false,
  oficinaPorta,
  oficinaTampa,
  oficinaTerceiraPorta,
  oficinaExtensor,
  aberturaPortas,
  aberturaTampaTraseira,
  deformacaoFrontal,
  deformacao,
  raioXEstrutural = false,
  ...props
}: Props) {
  return (
    <ProvedorDeformacao estado={deformacao} perfil={perfil} raioXEstrutural={raioXEstrutural}>
    <group {...props}>
      <EstruturaVeicular perfil={perfil} detalhesAnatomia={detalhesAnatomia} rebatimento={oficinaExtensor} />
      <DanoEstrutural3D perfil={perfil} />
      {mostrarSuspensao && (
        <SuspensaoVeicular
          perfil={perfil}
          detalhesAnatomia={detalhesAnatomia}
          compressaoDianteira={compressaoDianteira}
          compressaoTraseira={compressaoTraseira}
          destaque={destaqueSuspensao}
        />
      )}
      <ZonasColapsaveis perfil={perfil} />
      {mostrarMecanica && <ConjuntoMotriz perfil={perfil} detalhesAnatomia={detalhesAnatomia} />}
      {mostrarEnergia && <SistemasEnergia perfil={perfil} detalhesAnatomia={detalhesAnatomia} />}
      <Habitaculo perfil={perfil} deslocamentoPainel={oficinaExtensor?.elevacao ? oficinaExtensor.elevacao.alturaPainel * 0.35 : oficinaExtensor?.deslocamentoPainel ?? 0} elevacaoPainel={oficinaExtensor?.elevacao?.alturaPainel ?? (oficinaExtensor?.rebatimento ? oficinaExtensor.deslocamentoPainel * 0.4 : 0)} />
      <Bateria12V perfil={perfil} />

      <PortaDidatica
        perfil={perfil}
        id="portaDianteiraEsquerda"
        detalhesAnatomia={detalhesAnatomia}
        faixa={perfil.portaDianteira}
        lado="esquerdo"
        estadoOficina={oficinaPorta?.id === 'portaDianteiraEsquerda' ? oficinaPorta.estado : undefined}
        progressoSaida={oficinaPorta?.id === 'portaDianteiraEsquerda' ? oficinaPorta.progressoSaida : 1}
        progressoAcao={oficinaPorta?.id === 'portaDianteiraEsquerda' ? oficinaPorta.progressoAcao : 0}
        operacaoVisual={oficinaPorta?.id === 'portaDianteiraEsquerda' ? oficinaPorta.operacaoVisual : undefined}
        abertura={aberturaPortas?.portaDianteiraEsquerda}
      />
      <PortaDidatica
        perfil={perfil}
        id="portaDianteiraDireita"
        detalhesAnatomia={detalhesAnatomia}
        faixa={perfil.portaDianteira}
        lado="direito"
        estadoOficina={oficinaPorta?.id === 'portaDianteiraDireita' ? oficinaPorta.estado : undefined}
        progressoSaida={oficinaPorta?.id === 'portaDianteiraDireita' ? oficinaPorta.progressoSaida : 1}
        progressoAcao={oficinaPorta?.id === 'portaDianteiraDireita' ? oficinaPorta.progressoAcao : 0}
        operacaoVisual={oficinaPorta?.id === 'portaDianteiraDireita' ? oficinaPorta.operacaoVisual : undefined}
        abertura={aberturaPortas?.portaDianteiraDireita}
      />
      {oficinaTerceiraPorta?.lado === 'esquerdo'
        ? <PainelTerceiraPortaDidatico perfil={perfil} id="terceiraPortaEsquerda" faixa={perfil.portaTraseira} lado="esquerdo" estadoOficina={oficinaTerceiraPorta.estado} />
        : <PortaDidatica
            perfil={perfil}
            id="portaTraseiraEsquerda"
            detalhesAnatomia={detalhesAnatomia}
            faixa={perfil.portaTraseira}
            lado="esquerdo"
            estadoOficina={oficinaPorta?.id === 'portaTraseiraEsquerda' ? oficinaPorta.estado : undefined}
            progressoSaida={oficinaPorta?.id === 'portaTraseiraEsquerda' ? oficinaPorta.progressoSaida : 1}
            progressoAcao={oficinaPorta?.id === 'portaTraseiraEsquerda' ? oficinaPorta.progressoAcao : 0}
            operacaoVisual={oficinaPorta?.id === 'portaTraseiraEsquerda' ? oficinaPorta.operacaoVisual : undefined}
            abertura={aberturaPortas?.portaTraseiraEsquerda}
          />}
      {oficinaTerceiraPorta?.lado === 'direito'
        ? <PainelTerceiraPortaDidatico perfil={perfil} id="terceiraPortaDireita" faixa={perfil.portaTraseira} lado="direito" estadoOficina={oficinaTerceiraPorta.estado} />
        : <PortaDidatica
            perfil={perfil}
            id="portaTraseiraDireita"
            detalhesAnatomia={detalhesAnatomia}
            faixa={perfil.portaTraseira}
            lado="direito"
            estadoOficina={oficinaPorta?.id === 'portaTraseiraDireita' ? oficinaPorta.estado : undefined}
            progressoSaida={oficinaPorta?.id === 'portaTraseiraDireita' ? oficinaPorta.progressoSaida : 1}
            progressoAcao={oficinaPorta?.id === 'portaTraseiraDireita' ? oficinaPorta.progressoAcao : 0}
            operacaoVisual={oficinaPorta?.id === 'portaTraseiraDireita' ? oficinaPorta.operacaoVisual : undefined}
            abertura={aberturaPortas?.portaTraseiraDireita}
          />}

      {/* uma retenção por posição que o perfil declara, dianteira ou traseira */}
      {ancorasComCinto(perfil).map((ancora) => (
        <CintoSeguranca
          key={ancora.id}
          perfil={perfil}
          ancora={ancora}
          config={retencaoDaAncora(perfil, ancora.id)!}
        />
      ))}
      <SistemaSRS perfil={perfil} />

      {/* a carroceria vem por último: é translúcida e não deve escrever profundidade antes dos elementos didáticos */}
      <Carroceria
        perfil={perfil}
        detalhesAnatomia={detalhesAnatomia}
        mostrarRodas={mostrarRodas}
        distanciaRodada={distanciaRodada}
        oficinaPorta={oficinaPorta}
        oficinaTampa={oficinaTampa}
        tetoLateral={oficinaExtensor?.tetoLateral}
        aberturaTampa={aberturaTampaTraseira}
        deformacaoFrontal={deformacaoFrontal}
      />
    </group>
    </ProvedorDeformacao>
  )
}

export function HatchDidatico(props: Omit<Props, 'perfil'>) {
  return <VeiculoDidatico perfil={HATCH_COMPACTO} {...props} />
}

export function SedanDidatico(props: Omit<Props, 'perfil'>) {
  return <VeiculoDidatico perfil={SEDAN_MEDIO} {...props} />
}

export function VeiculoAlvoCompleto({
  perfil,
  corCarroceria,
  mostrarRodas = true,
  distanciaRodada = 0,
  mostrarSuspensao = true,
  mostrarMecanica = false,
  mostrarEnergia = false,
  deformacaoFrontal,
  deformacao,
  ...props
}: Props & { corCarroceria?: string }) {
  return (
    <ProvedorDeformacao estado={deformacao} perfil={perfil}>
    <group {...props}>
      <Carroceria
        perfil={perfil}
        modoAlvo
        corExterna={corCarroceria}
        mostrarRodas={mostrarRodas}
        distanciaRodada={distanciaRodada}
        deformacaoFrontal={deformacaoFrontal}
      />
      {[-1, 1].flatMap(sinal => [perfil.eixoDianteiroZ, perfil.eixoTraseiroZ].map(z => (
        <CaixaDeRoda3D key={`${sinal}:${z}`} modoAlvo
          id={`caixaRoda${z === perfil.eixoDianteiroZ ? 'Dianteira' : 'Traseira'}${sinal > 0 ? 'Esquerda' : 'Direita'}`}
          x={xDaEstrutura(perfil, sinal) - sinal * 0.06} y={perfil.raioRoda} z={z}
          raio={raioArcoRoda(perfil)} afastamento={[sinal * 0.45, 0, 0]} />
      )))}
      {mostrarSuspensao && <SuspensaoVeicular perfil={perfil} />}
    </group>
    </ProvedorDeformacao>
  )
}


